/**
 * Meeting Request Campaign Templates
 * Based on best practices from Calendly, Chili Piper, Gong, Drift
 *
 * Key insights from meeting scheduling leaders:
 * - Emails with calendar links get 27% more meetings booked
 * - Specific time slots outperform open-ended "let me know when you're free"
 * - Subject lines with the word "meeting" have higher open rates for warm leads
 * - Tuesday-Thursday at 10am-11am are peak booking times
 * - Mobile-friendly calendar links are essential (60% open on mobile)
 */

import { CampaignTemplate } from "./types";

export const MEETING_DIRECT_ASK: CampaignTemplate = {
  id: "meeting-direct-ask",
  name: "Direct Meeting Request",
  category: "meeting",
  description: "Simple, direct ask for a demo or discovery call.",
  strategy:
    "Based on Drift's finding that direct asks with clear value props convert 25% better than vague meeting requests. Gets straight to the point with a specific reason to meet and frictionless booking.",
  icon: "CalendarOutlined",
  color: "#8b5cf6",
  targetAudience: "b2b-saas",
  personalizationLevel: "basic",

  expectedOutcomes: [
    "25-35% open rate",
    "8-12% click on calendar link",
    "3-5% meeting booked rate",
    "Quick qualification of interest",
  ],

  bestPractices: [
    "Lead with the benefit of the meeting, not the ask",
    "Offer 2-3 specific time slots OR calendar link",
    "Keep email under 100 words",
    "Make the CTA impossible to miss",
    "Follow up with calendar reminder",
  ],

  objectionHandling: [
    "'Too busy' → Offer 15-min call instead of 30-min demo",
    "'Send me info first' → Share 2-min video then rebook",
    "'Not the right person' → Ask for intro to right person",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500"],
    jobTitles: [
      "VP Marketing",
      "Head of Sales",
      "Director of Customer Success",
      "CMO",
      "CEO",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Direct Ask",
      subject: "15 min this week, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-directAsk",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Direct meeting request with clear value proposition",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "meeting_duration",
          defaultValue: "15 minutes",
          description: "Length of proposed meeting",
        },
        {
          key: "meeting_purpose",
          defaultValue: "show you how companies like {{company_name}} are automating customer conversations",
          description: "Why they should meet",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Quick call about {{company_name}}'s chatbot strategy?",
          "{{first_name}} - 15 min to save you hours?",
        ],
        ctaVariants: [
          "Book time here",
          "Pick a time that works",
          "Grab 15 minutes",
        ],
      },
    },
    {
      name: "Day 3 - Time Options",
      subject: "Re: 15 min this week?",
      suggestedMjmlTemplateId: "coldOutreach-timeOptions",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Offer specific time slots as alternative to calendar link",
      variables: [
        {
          key: "time_option_1",
          defaultValue: "Tuesday at 10am",
          description: "First time option",
        },
        {
          key: "time_option_2",
          defaultValue: "Wednesday at 2pm",
          description: "Second time option",
        },
        {
          key: "time_option_3",
          defaultValue: "Thursday at 11am",
          description: "Third time option",
        },
      ],
    },
    {
      name: "Day 7 - Calendar Bump",
      subject: "My calendar for you, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-calendarBump",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share calendar link with easy booking message",
    },
    {
      name: "Day 12 - Final Attempt",
      subject: "Should I stop asking?",
      suggestedMjmlTemplateId: "coldOutreach-finalMeetingAsk",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final meeting request with breakup tone",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "{{user_title}}",
      description: "Sales rep's title",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Calendly or booking link",
      source: "crm",
    },
    {
      key: "demo_url",
      defaultValue: "{{booking_link}}",
      description: "Demo scheduling link",
      source: "crm",
    },
  ],

  tags: ["meeting", "demo", "direct-ask", "calendar", "booking"],
  bestFor: ["SDR Teams", "Transactional Sales", "SMB Sales"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["Drift", "Calendly", "Chili Piper"],
};

export const MEETING_PERSONALIZED: CampaignTemplate = {
  id: "meeting-personalized",
  name: "Hyper-Personalized Meeting",
  category: "meeting",
  description:
    "Meeting requests with deep personalization based on research.",
  strategy:
    "Based on Outreach data showing personalized meeting requests have 2.5x higher acceptance rates. Each email references specific details about the prospect's company, role, or recent activity to demonstrate genuine interest.",
  icon: "UserOutlined",
  color: "#ec4899",
  targetAudience: "enterprise",
  personalizationLevel: "hyper",

  expectedOutcomes: [
    "35-45% open rate",
    "12-18% reply rate",
    "15-20% meeting acceptance",
    "Higher quality conversations",
  ],

  bestPractices: [
    "Spend 3-5 minutes researching before sending",
    "Reference specific content they've published or shared",
    "Connect your solution to their stated priorities",
    "Mention mutual connections if available",
    "Acknowledge their expertise genuinely",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event", "website"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Professional Services"],
    jobTitles: ["VP", "Director", "C-Level", "Head of", "Founder"],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "job_title",
      "industry",
    ],
  },

  emails: [
    {
      name: "Day 0 - Personal Connection",
      subject: "{{trigger_event}} - congrats, {{first_name}}!",
      suggestedMjmlTemplateId: "coldOutreach-personalConnection",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Open with personalized observation, connect to meeting",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "trigger_event",
          defaultValue: "Your recent funding round",
          description: "Specific event/trigger researched",
        },
        {
          key: "personalized_observation",
          defaultValue: "I noticed {{company_name}} is expanding into new markets",
          description: "Specific observation about prospect",
        },
        {
          key: "relevant_connection",
          defaultValue: "We helped similar companies scale their customer engagement",
          description: "How your solution relates",
        },
      ],
    },
    {
      name: "Day 4 - Content Reference",
      subject: "Re: Your post about {{content_topic}}",
      suggestedMjmlTemplateId: "coldOutreach-contentReference",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Reference content they've published, add your perspective",
      variables: [
        {
          key: "content_topic",
          defaultValue: "customer experience",
          description: "Topic they've written about",
        },
        {
          key: "content_source",
          defaultValue: "LinkedIn",
          description: "Where you saw their content",
        },
      ],
    },
    {
      name: "Day 9 - Mutual Connection",
      subject: "{{mutual_connection}} suggested I reach out",
      suggestedMjmlTemplateId: "coldOutreach-mutualConnection",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Leverage mutual connection or shared experience",
      variables: [
        {
          key: "mutual_connection",
          defaultValue: "Our mutual connection at {{shared_company}}",
          description: "Shared connection or experience",
        },
      ],
    },
    {
      name: "Day 14 - Company News",
      subject: "Saw {{company_name}} in the news - thoughts?",
      suggestedMjmlTemplateId: "coldOutreach-companyNews",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Reference recent company news, offer relevant insight",
      variables: [
        {
          key: "recent_news",
          defaultValue: "your product launch",
          description: "Recent company news item",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 30,
    dailyLimit: 90,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_linkedin",
      defaultValue: "{{user_linkedin}}",
      description: "Sales rep's LinkedIn profile",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Calendly or booking link",
      source: "crm",
    },
    {
      key: "trigger_event",
      defaultValue: "your recent announcement",
      description: "Personalized trigger event",
      source: "enrichment",
    },
    {
      key: "mutual_connection",
      defaultValue: "a colleague",
      description: "Shared connection name",
      source: "enrichment",
    },
  ],

  tags: ["meeting", "personalized", "research-based", "high-touch", "enterprise"],
  bestFor: ["Enterprise Sales", "Strategic Accounts", "Complex Deals"],
  difficulty: "advanced",
  setupTime: "30 minutes",
  inspiredBy: ["Outreach", "LinkedIn Sales Navigator", "Gong"],
};

export const MEETING_CALENDAR_LINK: CampaignTemplate = {
  id: "meeting-calendar-link",
  name: "Frictionless Calendar Booking",
  category: "meeting",
  description: "Minimize friction with prominent calendar link CTAs.",
  strategy:
    "Based on Calendly's data showing that one-click booking increases meeting conversions by 47%. Every email features a prominent, mobile-friendly calendar link with minimal text. Removes all barriers between interest and booking.",
  icon: "ScheduleOutlined",
  color: "#3b82f6",
  targetAudience: "b2b-saas",
  personalizationLevel: "basic",

  expectedOutcomes: [
    "Higher click-to-book conversion",
    "Reduced back-and-forth on scheduling",
    "Better mobile experience",
    "Faster sales cycle",
  ],

  bestPractices: [
    "Use button CTA, not text link",
    "Test on mobile before sending",
    "Include timezone detection",
    "Offer multiple meeting durations",
    "Send calendar reminder after booking",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "referral", "event"],
    companySize: ["1-10", "11-50", "51-200"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - One-Click Book",
      subject: "{{first_name}}, pick a time",
      suggestedMjmlTemplateId: "coldOutreach-oneClickBook",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Ultra-short email with prominent calendar button",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "calendar_button_text",
          defaultValue: "Book 15 Minutes",
          description: "Text on calendar button",
        },
      ],
      abTestSuggestions: {
        ctaVariants: [
          "Book 15 Minutes",
          "Schedule a Quick Chat",
          "Pick a Time",
          "See My Calendar",
        ],
      },
    },
    {
      name: "Day 3 - Flexibility",
      subject: "My calendar is open",
      suggestedMjmlTemplateId: "coldOutreach-calendarOpen",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Emphasize flexibility and ease of booking",
    },
    {
      name: "Day 7 - Quick Options",
      subject: "3 times that might work",
      suggestedMjmlTemplateId: "coldOutreach-quickOptions",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer specific times alongside calendar link",
      variables: [
        {
          key: "option_1",
          defaultValue: "Tomorrow at 10am",
          description: "First time option",
        },
        {
          key: "option_2",
          defaultValue: "Thursday at 2pm",
          description: "Second time option",
        },
        {
          key: "option_3",
          defaultValue: "Friday at 11am",
          description: "Third time option",
        },
      ],
    },
    {
      name: "Day 12 - Last Slot",
      subject: "Holding a spot for you",
      suggestedMjmlTemplateId: "coldOutreach-lastSlot",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Create gentle urgency with 'held spot' framing",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Calendly or booking link",
      source: "crm",
    },
    {
      key: "meeting_duration",
      defaultValue: "15 minutes",
      description: "Default meeting length",
      source: "manual",
    },
  ],

  tags: ["meeting", "calendar", "frictionless", "booking", "mobile-friendly"],
  bestFor: ["High-Volume Sales", "SMB Sales", "Transactional Deals"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["Calendly", "Chili Piper", "HubSpot Meetings"],
};

export const MEETING_PEER_INTRO: CampaignTemplate = {
  id: "meeting-peer-intro",
  name: "Peer Introduction Request",
  category: "meeting",
  description: "Request introduction to the right person through a peer.",
  strategy:
    "Based on research showing referred meetings have 4x higher show rates. Instead of going direct to the decision-maker, start with a peer who can make an internal introduction. Reduces cold outreach friction and builds credibility.",
  icon: "TeamOutlined",
  color: "#06b6d4",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "Higher response rate from non-decision makers",
    "Warm introduction to actual buyer",
    "Internal champion development",
    "Better meeting quality",
  ],

  bestPractices: [
    "Target someone who benefits from your solution",
    "Make it easy for them to forward your message",
    "Acknowledge their expertise in their domain",
    "Offer value to them, not just their boss",
    "Keep the forward message simple",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "event"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    jobTitles: [
      "Manager",
      "Senior",
      "Lead",
      "Specialist",
      "Analyst",
      "Coordinator",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Peer Outreach",
      subject: "Quick question, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-peerOutreach",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Reach out to peer, ask who handles [area]",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "area_of_interest",
          defaultValue: "customer support automation",
          description: "The area you want to discuss",
        },
        {
          key: "value_for_peer",
          defaultValue: "I have some research that might help your team",
          description: "What's in it for them",
        },
      ],
    },
    {
      name: "Day 4 - Value for Them",
      subject: "Something for your team, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-valueForPeer",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer value to them directly, reiterate intro request",
    },
    {
      name: "Day 9 - Easy Forward",
      subject: "Feel free to forward this",
      suggestedMjmlTemplateId: "coldOutreach-easyForward",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Provide pre-written forward message for them to send",
      variables: [
        {
          key: "forward_message",
          defaultValue: "Hey [Name], this might be worth a quick look...",
          description: "Pre-written forward text",
        },
      ],
    },
    {
      name: "Day 14 - Direct Ask",
      subject: "Mind pointing me in the right direction?",
      suggestedMjmlTemplateId: "coldOutreach-directionAsk",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final ask for direction to right person",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "{{user_title}}",
      description: "Sales rep's title",
      source: "crm",
    },
    {
      key: "area_of_interest",
      defaultValue: "customer engagement",
      description: "What you want to discuss",
      source: "manual",
    },
    {
      key: "decision_maker_title",
      defaultValue: "VP of Marketing",
      description: "Title of person you want to reach",
      source: "manual",
    },
  ],

  tags: ["meeting", "referral", "peer-intro", "champion-building", "enterprise"],
  bestFor: ["Enterprise Sales", "Large Organizations", "Complex Buying Committees"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Challenger Sale", "SPIN Selling", "Account-Based Everything"],
};

export const MEETING_EXECUTIVE_BRIEFING: CampaignTemplate = {
  id: "meeting-executive-briefing",
  name: "Executive Briefing Invitation",
  category: "meeting",
  description: "Invite executives to an exclusive briefing or strategy session.",
  strategy:
    "Based on Salesforce's approach of offering exclusive executive briefings instead of demos. Positions the meeting as a strategic discussion between peers rather than a sales pitch. Works best when sender has similar seniority or industry expertise.",
  icon: "CrownOutlined",
  color: "#f59e0b",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "15-25% response rate from C-level",
    "Higher quality strategic conversations",
    "Direct access to decision makers",
    "Stronger executive relationships",
  ],

  bestPractices: [
    "Send from senior person (VP or higher)",
    "Frame as peer-to-peer, not vendor-buyer",
    "Offer genuine strategic value, not a pitch",
    "Reference industry trends and challenges",
    "Be flexible on format (call, lunch, dinner)",
  ],

  objectionHandling: [
    "'Too busy' → Offer breakfast or dinner meeting instead",
    "'Send to my team' → Offer to brief team and circle back with exec summary",
    "'What's the catch?' → Be transparent it's a conversation, not a pitch",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event"],
    companySize: ["201-500", "501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    jobTitles: ["CEO", "CTO", "CMO", "CRO", "CFO", "COO", "President", "Founder"],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Executive Invitation",
      subject: "{{first_name}}, executive briefing on {{industry_topic}}",
      suggestedMjmlTemplateId: "coldOutreach-executiveInvite",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Invite to exclusive executive briefing on strategic topic",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "industry_topic",
          defaultValue: "AI in customer engagement",
          description: "Strategic topic for briefing",
        },
        {
          key: "briefing_format",
          defaultValue: "30-minute virtual briefing",
          description: "Format of the meeting",
        },
        {
          key: "sender_credentials",
          defaultValue: "I've helped 50+ companies navigate this transition",
          description: "Why sender is credible",
        },
      ],
    },
    {
      name: "Day 5 - Industry Insight",
      subject: "What I'm seeing in {{industry}}",
      suggestedMjmlTemplateId: "coldOutreach-industryInsight",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share exclusive industry insight, reinforce briefing value",
      variables: [
        {
          key: "insight_preview",
          defaultValue: "3 trends reshaping customer engagement in 2024",
          description: "Preview of briefing content",
        },
      ],
    },
    {
      name: "Day 11 - Peer Reference",
      subject: "What {{peer_exec}} said after our briefing",
      suggestedMjmlTemplateId: "coldOutreach-peerReference",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Share feedback from peer executive who attended briefing",
      variables: [
        {
          key: "peer_exec",
          defaultValue: "the CMO at TechCorp",
          description: "Peer executive reference",
        },
        {
          key: "peer_quote",
          defaultValue: "This gave me a completely new perspective on our strategy",
          description: "Quote from peer exec",
        },
      ],
    },
    {
      name: "Day 18 - Alternative Format",
      subject: "Coffee or call, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-alternativeFormat",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Offer alternative format - more casual conversation",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 20,
    dailyLimit: 60,
    requiresWarming: true,
    minDomainAge: 60,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name (should be executive)",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "VP of Customer Success",
      description: "Sender's title (should be senior)",
      source: "crm",
    },
    {
      key: "industry_topic",
      defaultValue: "AI transformation",
      description: "Strategic topic for briefing",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
    {
      key: "industry",
      defaultValue: "your industry",
      description: "Prospect's industry",
      source: "enrichment",
    },
  ],

  tags: ["meeting", "executive", "briefing", "c-level", "strategic"],
  bestFor: ["Enterprise Sales", "C-Level Engagement", "Strategic Deals"],
  difficulty: "advanced",
  setupTime: "25 minutes",
  inspiredBy: ["Salesforce", "McKinsey", "Gartner"],
};

export const MEETING_TEMPLATES = [
  MEETING_DIRECT_ASK,
  MEETING_PERSONALIZED,
  MEETING_CALENDAR_LINK,
  MEETING_PEER_INTRO,
  MEETING_EXECUTIVE_BRIEFING,
];
